import React, { useEffect, useRef, useState } from "react"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js"
import { useSelector } from "react-redux"
import useProfile from "@hooks/useProfile"
import { parseISO } from "date-fns"
import { useParams } from "react-router"
import { Tab, Tabs, TabsList } from "@mui/base"
import { GRAPH_TABS } from "./helper"
import styles from "./GraphComponent.module.css"
import LineChart from "../LineChart/LineChart"
import BarChart from "../BarChart/BarChart"
import useWindowSize from "@hooks/useWindowSize"
import Chart from "@assets/ReportPage/Chart.svg"
import ActivityChart from "../ActivityChart/ActivityChart"

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
)

const GraphComponent = () => {
    const [filterSelected, setFilterSelected] = useState("correctness")
    const { handleGetOwnDetails } = useProfile()
    const { profileDetails } = useSelector((state) => state.profile)
    const [chartData, setChartData] = useState(null)
    const [chartData2, setChartData2] = useState(null)
    const boxRef = useRef(null)
    const { width } = useWindowSize()
    const [activityData, setActivityData] = useState(null)

    const { gameDetails } = useParams()
    const [game, path] = gameDetails.split("@")

    // dynamically assign the width of the box based on the number of labels
    const getBoxWidth = () => {
        const numberOfLabels = chartData?.labels?.length
        const boxWidth = numberOfLabels * 110

        return boxWidth
    }

    useEffect(() => {
        handleGetOwnDetails()
    }, [handleGetOwnDetails])

    useEffect(() => {
        if (
            profileDetails &&
            profileDetails.exercises[path][game] &&
            profileDetails.exercises[path][game].scoreHistory
        ) {
            const scoreHistory =
                profileDetails.exercises[path][game].scoreHistory

            const labels = scoreHistory.map((item) => {
                if (item.created_at) {
                    const date = parseISO(item.created_at)
                    return date.toLocaleDateString('en-GB')
                }
                return "Unknown Date"
            })

            let dataset1, dataset2

            switch (filterSelected) {
                case "accuracyandprecision":
                    dataset1 = {
                        label: "Accuracy",
                        data: scoreHistory.map(
                            (item) => item.accuracy.$numberDecimal
                        ),
                    }
                    dataset2 = {
                        label: "Precision",
                        data: scoreHistory.map(
                            (item) => item.precision.$numberDecimal
                        ),
                    }
                    break
                case "precision":
                    dataset1 = {
                        label: "Precision",
                        data: scoreHistory.map(
                            (item) => item.precision.$numberDecimal
                        ),
                    }
                    break
                case "level":
                    dataset1 = {
                        label: "Level",
                        data: scoreHistory.map((item) => item.level),
                    }
                    break
                case "correctness":
                default:
                    dataset1 = {
                        label: "Correct",
                        data: scoreHistory.map((item) => item.correct),
                    }
                    dataset2 = {
                        label: "Incorrect",
                        data: scoreHistory.map((item) => item.incorrect),
                    }
            }

            setActivityData(labels);

            setChartData2({
                labels,
                datasets: [{ ...dataset1 }, { ...dataset2 }],
            })

            setChartData({
                labels,
                datasets: [
                    {
                        ...dataset1,
                        borderColor: "rgba(99, 177, 252, 1)",
                        backgroundColor: "rgba(99, 177, 252, 1)",
                        barThickness: 50,
                        pointRadius: 6,
                        pointHoverRadius: 8,
                    },
                    {
                        ...dataset2,
                        borderColor: "rgba(0, 91, 178, 1)",
                        backgroundColor: "rgba(0, 91, 178, 1)",
                        barThickness: 50,
                        pointRadius: 6,
                        pointHoverRadius: 8,
                    },
                ],
            })
        }
    }, [profileDetails, game, filterSelected, path])

    const options2 = {
        maintainAspectRatio: false,
        layout: {
            padding: {
                bottom: 36.23,
            },
        },
        responsive: true,
        interaction: {
            mode: "index",
            intersect: false,
        },

        stacked: false,
        plugins: {
            title: {
                display: false,
                text: "Game Performance Over Time",
            },
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "",
                },
                ticks: {
                    display: false,
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                },
            },
            y: {
                beginAtZero: false,
                afterFit: (ctx) => {
                    if (width > 770) {
                        ctx.width = 38
                    } else if (width < 770) {
                        ctx.width = 29
                    } else if (width < 600) {
                        ctx.width = 20
                    }
                },

                type: "linear",
                display: true,
                position: "left",
                title: {
                    display: false,
                    text: "Number of Answers",
                },
                ticks: {
                    display: true,
                },
            },
        },
    }
    const options1 = {
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 10,
            },
        },
        responsive: true,
        interaction: {
            mode: "index",
            intersect: false,
        },

        stacked: false,
        plugins: {
            title: {
                display: false,
                text: "Game Performance Over Time",
            },
            legend: {
                display: false,
            },
        },

        scales: {
            x: {
                title: {
                    display: true,
                    text: "",
                },
                ticks: {
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                },
            },
            y: {
                type: "linear",
                display: true,
                position: "left",
                title: {
                    display: false,
                    text: "Number of Answers",
                },
                ticks: {
                    display: false,
                },
                grid: {
                    drawTicks: false,
                    drawBorder: false,
                },
            },
        },
    }
    const handleChange = (event, newValue) => {
        setFilterSelected(newValue)
    }

    return (
        <>
            <div
                className={`${styles.Wrapper} flex flex-col gap-4 justify-center items-center`}
            >
                <div className="flex justify-between w-full flex-wrap gap-4">
                    <div className="flex gap-2 items-center">
                        <img src={Chart} alt="chart insights" />
                        <div className="flex flex-col gap-1 text-[var(--text-light-gray)]">
                            <h2 className="uppercase text-xs font-bold tracking-widest">
                                {" "}
                                Chart Insights
                            </h2>
                            <span className="text-xs">
                                Select to view charts
                            </span>
                        </div>
                    </div>
                    <Tabs
                        defaultValue={"correctness"}
                        value={filterSelected}
                        onChange={handleChange}
                        className="min-w relative"
                    >
                        <TabsList className="flex items-center gap-4 flex-wrap relative">
                            {GRAPH_TABS.map((tab) => (
                                <Tab
                                    value={tab.key}
                                    key={tab.key}
                                    className={`${tab.key === filterSelected ? "text-white bg-[var(--bg-theme-primary)]" : "text-[var(--text-light-gray)] bg-[var(--pure-white)]"} flex items-center justify-center flex-shrink-0 text-nowrap py-1.5 px-4  text-xs rounded-full font-semibold h-full`}
                                >
                                    {tab.label}
                                </Tab>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                {chartData &&
                (filterSelected === "correctness" ||
                    filterSelected === "precision" ||
                    filterSelected === "level") ? (
                    <LineChart
                        options1={options1}
                        chartData={chartData}
                        options2={options2}
                        chartData2={chartData2}
                        boxRef={boxRef}
                        getBoxWidth={getBoxWidth}
                    />
                ) : filterSelected === "accuracyandprecision" ? (
                    <BarChart
                        options1={options1}
                        chartData={chartData}
                        options2={options2}
                        chartData2={chartData2}
                        boxRef={boxRef}
                        getBoxWidth={getBoxWidth}
                    />
                ) : filterSelected === "activity" ? (
                    <ActivityChart data={activityData}/>
                ): null}
            </div>
        </>
    )
}

export default GraphComponent
